/** Color key and horizontal scale for the reservation Payments tab. */

export const PX_PER_HOUR = 8;
export const TIMELINE_PAD = 16;
const TRACK_PITCH = 16;
const LANE_PAD = 8;

export const PAYMENT_COLORS = [
  "#1d4e89",
  "#c44900",
  "#0f766e",
  "#6d28d9",
  "#b42318",
  "#3f6212",
  "#0369a1",
  "#b45309",
  "#9d174d",
  "#155e75",
  "#4d7c0f",
  "#7c3aed",
];

const OFFSET_HOURS = {
  EDT: -4,
  EST: -5,
  CDT: -5,
  CST: -6,
  MDT: -6,
  MST: -7,
  PDT: -7,
  PST: -8,
};

const ZONE_BY_ABBR = {
  EDT: "America/New_York",
  EST: "America/New_York",
  CDT: "America/Chicago",
  CST: "America/Chicago",
  MDT: "America/Denver",
  MST: "America/Denver",
  PDT: "America/Los_Angeles",
  PST: "America/Los_Angeles",
};

export function paymentColor(index) {
  return PAYMENT_COLORS[index % PAYMENT_COLORS.length];
}

export function parsePaymentStamp(text) {
  const match = String(text || "").trim().match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}) ([A-Z]{2,4})$/);
  if (!match) return null;
  const offset = OFFSET_HOURS[match[6]];
  const zone = ZONE_BY_ABBR[match[6]];
  if (offset == null || !zone) return null;
  const ms = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]) - offset,
    Number(match[5]),
  );
  if (Number.isNaN(ms)) return null;
  return { ms, zone };
}

function segment(row, fromKey, throughKey) {
  const from = parsePaymentStamp(row[fromKey]);
  const through = parsePaymentStamp(row[throughKey]);
  if (!from || !through || through.ms <= from.ms) return null;
  return {
    row: row.index,
    color: row.color,
    start: from.ms,
    end: through.ms,
    zone: through.zone,
    title: (row.kind ? row.kind + " " : "") + row[fromKey] + " – " + row[throughKey],
  };
}

function pack(list) {
  const trackEnds = [];
  return list
    .slice()
    .sort((a, b) => a.start - b.start || a.row - b.row)
    .map((seg) => {
      let track = trackEnds.findIndex((end) => end <= seg.start);
      if (track < 0) {
        track = trackEnds.length;
        trackEnds.push(seg.end);
      } else {
        trackEnds[track] = seg.end;
      }
      return { ...seg, track };
    });
}

function place(list, origin) {
  return pack(list).map((seg) => {
    const left = TIMELINE_PAD + ((seg.start - origin) / 3600000) * PX_PER_HOUR;
    const width = ((seg.end - seg.start) / 3600000) * PX_PER_HOUR;
    return { ...seg, left, width, top: 4 + seg.track * TRACK_PITCH };
  });
}

function laneHeight(bars) {
  const tracks = bars.reduce((max, bar) => Math.max(max, bar.track + 1), 1);
  return LANE_PAD + tracks * TRACK_PITCH;
}

function offsetMinutes(ms, zone) {
  const name = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "shortOffset",
    hour: "2-digit",
  }).formatToParts(new Date(ms)).find((part) => part.type === "timeZoneName");
  const match = String(name && name.value).match(/GMT([+-])(\d+)(?::(\d+))?/);
  if (!match) return 0;
  const sign = match[1] === "-" ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3] || 0));
}

function zoned(year, month, day, hour, minute, zone) {
  const utc = Date.UTC(year, month - 1, day, hour, minute);
  const off = offsetMinutes(utc, zone);
  return Date.UTC(year, month - 1, day, hour, minute) - off * 60000;
}

function localParts(ms, zone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(ms));
  const get = (type) => Number(parts.find((part) => part.type === type).value);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") };
}

function ticks(start, end, zone) {
  const spanHours = (end - start) / 3600000;
  const stepHours = spanHours <= 36 ? 6 : 24;
  const first = localParts(start, zone);
  let cursor = zoned(first.year, first.month, first.day, stepHours === 24 ? 0 : first.hour, 0, zone);
  if (cursor < start) {
    if (stepHours === 24) {
      const next = localParts(cursor + 36 * 3600000, zone);
      cursor = zoned(next.year, next.month, next.day, 0, 0, zone);
    } else {
      cursor += stepHours * 3600000;
    }
  }
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    month: "short",
    day: "numeric",
    hour: stepHours < 24 ? "numeric" : undefined,
  });
  const marks = [];
  while (cursor <= end && marks.length < 400) {
    marks.push({
      ms: cursor,
      left: TIMELINE_PAD + ((cursor - start) / 3600000) * PX_PER_HOUR,
      label: format.format(new Date(cursor)),
    });
    if (stepHours === 24) {
      const next = localParts(cursor + 36 * 3600000, zone);
      cursor = zoned(next.year, next.month, next.day, 0, 0, zone);
    } else {
      cursor += stepHours * 3600000;
    }
  }
  return marks;
}

export function layoutPaymentTimeline(rows) {
  const colored = (rows || []).map((row, index) => ({
    ...row,
    index,
    color: paymentColor(index),
  }));
  const rental = [];
  const insurance = [];
  let zone = "America/Chicago";
  colored.forEach((row) => {
    const rent = segment(row, "rentalFrom", "rentalThrough");
    const cover = segment(row, "insuranceFrom", "insuranceThrough");
    if (rent) {
      rental.push(rent);
      zone = rent.zone;
    }
    if (cover) {
      insurance.push(cover);
      zone = cover.zone;
    }
  });
  const all = rental.concat(insurance);
  if (!all.length) return null;
  const start = Math.min(...all.map((seg) => seg.start));
  const end = Math.max(...all.map((seg) => seg.end));
  const span = Math.max(end - start, 3600000);
  const rentalBars = place(rental, start);
  const insuranceBars = place(insurance, start);
  return {
    start,
    end,
    width: TIMELINE_PAD * 2 + (span / 3600000) * PX_PER_HOUR,
    rental: rentalBars,
    insurance: insuranceBars,
    rentalHeight: laneHeight(rentalBars),
    insuranceHeight: laneHeight(insuranceBars),
    ticks: ticks(start, end, zone),
  };
}
